/* eslint-disable react/prop-types */
/* eslint-disable no-console */
import { useEffect, useState } from 'react';

import { setUpdateUserTokenCallback, cleanup } from '../authService';

import { LoaderUI } from './LoaderUI';
import type {
  MicroFrontendLoaderProps,
  ChatModuleMountResult,
  ExtendedWindow,
  ChatModule,
  StaticTrigger,
} from './types';
import { ARIES_BUNDLE_URL, waitForContainer } from './utils';

const MicroFrontendLoader: React.FC<MicroFrontendLoaderProps> = ({
  productKey,
  isWidgetMode,
  featureFlags,
  customArn,
  targetStage,
  region,
  userToken: providedUserToken,
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Clean up any previous micro-frontend instance
    try {
      const win = window as ExtendedWindow;

      // Check if unmountMicroApp exists and is a function
      if (win.unmountMicroApp && typeof win.unmountMicroApp === 'function') {
        win.unmountMicroApp();
      }

      // Check if unmountTrigger exists and is a function
      if (win.unmountTrigger && typeof win.unmountTrigger === 'function') {
        win.unmountTrigger();
      }

      // Delete window properties to prevent stale references
      const winObj = window as unknown as Record<string, unknown>;
      delete winObj.unmountMicroApp;
      delete winObj.unmountTrigger;
      delete winObj.updateToken;
      delete winObj.updateUserToken;
      delete winObj.sendAgentPrompt;
      delete winObj.mount;
      delete winObj.onUserPropMutate;
    } catch (error) {
      console.error('Error cleaning up previous instance:', error);
    }

    setIsLoaded(false);
    setError(null);

    let disposed = false;

    const loadMicroFrontend = async () => {
      try {
        // Set up the callback for user token updates
        setUpdateUserTokenCallback((newToken) => {
          if (window.updateUserToken) {
            window.updateUserToken(newToken);
          }
        });

        const token = 'something';
        const userToken = providedUserToken;
        if (!userToken) {
          throw new Error(
            'UP token is required. Enter one in the host app sidebar before mounting Aries.'
          );
        }

        // Wait for the container to be ready
        await waitForContainer(isWidgetMode);
        if (disposed) return;

        // Load the Aries bundle
        if (!ARIES_BUNDLE_URL) {
          throw new Error('There is no URL for the ARIES bundle');
        }

        // Import the chat module and static trigger
        const [chatModuleImport, staticTriggerImport] = await Promise.all([
          import(`${ARIES_BUNDLE_URL}/chatModule.js?product=grc_agents`),
          import(`${ARIES_BUNDLE_URL}/staticTrigger.js?product=grc_agents`),
        ]);
        if (disposed) return;

        // Cast the dynamic imports to the expected types and validate
        const chatModule = chatModuleImport as unknown as ChatModule;
        const staticTrigger = staticTriggerImport as unknown as StaticTrigger;

        // Verify that required functions exist
        if (typeof chatModule.mount !== 'function') {
          throw new Error('chatModule.mount is not a function');
        }

        // Create the configuration object
        const containerId = 'product-provided-container';

        const config = {
          containerId: isWidgetMode ? undefined : containerId,
          featuresList: [],
          headerId: 'unique-id',
          supportUrl: '#',
          triggerType: 'floating',
          featureFlags,
          arn: customArn,
          targetStage,
          region,
        };

        // Create the context object with the extracted user info
        const context = {
          productId: productKey || 'default_product',
          token,
          userToken,
          user: {
            firstName: 'John',
            lastName: 'Doe',
            userId: 'user-123',
            emailAddress: 'john.doe@example.com',
          },
        };

        let unmount = () => {};
        let mountResult: ChatModuleMountResult | undefined;
        if (typeof chatModule.mount === 'function') {
          mountResult = await chatModule.mount({ config, context });

          // Ensure the returned unmount is a function
          if (mountResult && typeof mountResult.unmount === 'function') {
            unmount = mountResult.unmount;
          }
        }

        if (disposed) {
          unmount();
          return;
        }

        // Mount the trigger if in widget mode
        let unmountTrigger = () => {};
        if (isWidgetMode && typeof staticTrigger.mount === 'function') {
          const unmountFn = await staticTrigger.mount({
            containerId: 'aries-trigger',
            productId: context.productId,
            userId: context.user.userId,
          });

          // Ensure the returned value is a function
          if (typeof unmountFn === 'function') {
            unmountTrigger = unmountFn;
          }
        }

        if (disposed) {
          // A newer effect run already cleaned up while this mount was in
          // flight (e.g. a rapid region switch) — tear down this stale
          // instance instead of exposing it on window over the current one.
          unmount();
          unmountTrigger();
          return;
        }

        // Expose functions to the window using our typed interface
        const win = window as ExtendedWindow;
        if (chatModule.updateToken) win.updateToken = chatModule.updateToken;
        if (chatModule.updateUserToken) win.updateUserToken = chatModule.updateUserToken;
        win.unmountMicroApp = () => {
          cleanup();
          unmount();
        };
        win.unmountTrigger = unmountTrigger;

        if (chatModule.mount) win.mount = chatModule.mount;
        if (mountResult?.onUserPropMutate) win.onUserPropMutate = mountResult.onUserPropMutate;
        if (mountResult?.sendAgentPrompt) win.sendAgentPrompt = mountResult.sendAgentPrompt;

        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to load micro-frontend:', error);
        if (!disposed) {
          setError(error instanceof Error ? error.message : 'Unknown error');
        }
      }
    };

    loadMicroFrontend();

    // Clean up when the component unmounts or when dependencies change
    return () => {
      disposed = true;
      try {
        const win = window as ExtendedWindow;

        // Check if unmountMicroApp exists and is a function
        if (win.unmountMicroApp && typeof win.unmountMicroApp === 'function') {
          win.unmountMicroApp();
        }

        // Check if unmountTrigger exists and is a function
        if (win.unmountTrigger && typeof win.unmountTrigger === 'function') {
          win.unmountTrigger();
        }

        // Delete window properties to prevent stale references
        const winObj = window as unknown as Record<string, unknown>;
        delete winObj.unmountMicroApp;
        delete winObj.unmountTrigger;
        delete winObj.updateToken;
        delete winObj.updateUserToken;
        delete winObj.mount;
        delete winObj.onUserPropMutate;
      } catch (error) {
        console.error('Error during cleanup:', error);
      }
    };
  }, [productKey, isWidgetMode, customArn, targetStage, region, providedUserToken]);

  return <LoaderUI error={error} isLoaded={isLoaded} isWidgetMode={isWidgetMode} />;
};

export default MicroFrontendLoader;
